import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/utils/cn';
import type { SizeOptionWithEdd } from '../../domain/models/CustomizationOptions';
import type { SizeOption } from '../../utils/parseSizeList';
import { shouldShowSizeSheetEddColumn } from '../../utils/sizeSheetDisplay.utils';
import { CustomizationSizeEddLabel } from './CustomizationSizeEddLabel';

interface CustomizationDesktopSizeSectionProps {
  sizeLabel: string;
  placeholder: string;
  sizes: SizeOptionWithEdd[];
  selectedSize: SizeOption | null;
  hasPincode: boolean;
  nddAvailableSizes: string[] | null;
  isApplying: boolean;
  sizeNotSelected: boolean;
  shakeKey: number;
  sizerLinkLabel: string | null;
  onOpenSizer: () => void;
  onSelect: (size: SizeOptionWithEdd) => void;
}

function getDropdownColumnLabel(sizeLabel: string): string {
  return sizeLabel === 'Length (in inches)' ? 'Length' : 'Size';
}

export function CustomizationDesktopSizeSection({
  sizeLabel,
  placeholder,
  sizes,
  selectedSize,
  hasPincode,
  nddAvailableSizes,
  isApplying,
  sizeNotSelected,
  shakeKey,
  sizerLinkLabel,
  onOpenSizer,
  onSelect,
}: CustomizationDesktopSizeSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownColumnLabel = getDropdownColumnLabel(sizeLabel);
  const showEddColumn = shouldShowSizeSheetEddColumn(hasPincode, sizes);
  const dropdownWidth = showEddColumn ? 300 : 220;

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: dropdownWidth,
    });
  }, [dropdownWidth]);

  const handleToggle = useCallback(() => {
    if (isApplying) return;
    setIsOpen((prev) => !prev);
  }, [isApplying]);

  const handleSelect = useCallback(
    (size: SizeOptionWithEdd) => {
      onSelect(size);
      setIsOpen(false);
    },
    [onSelect]
  );

  useLayoutEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const selectedEl = listRef.current.querySelector('[data-selected="true"]');
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [isOpen]);

  return (
    <div
      id="pdp-size-section"
      className=""
    >
      <div className="flex items-center gap-4">
        <span
          key={shakeKey}
          className={cn(
            'shrink-0 text-sm text-[#666]',
            sizeNotSelected && 'text-red-600',
            shakeKey > 0 && 'animate-shake'
          )}
        >
          {sizeLabel}
        </span>

        <div ref={containerRef} className="relative w-[180px] shrink-0">
          <button
            ref={triggerRef}
            type="button"
            onClick={handleToggle}
            disabled={isApplying}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            className={cn(
              'relative flex w-full items-center rounded border border-[#d9d9d9] bg-white px-2.5 py-2.5 text-left text-[13px] leading-snug text-black',
              sizeNotSelected && 'border-red-500'
            )}
          >
            <span className="truncate pr-5">
              {selectedSize?.value ?? `Select ${placeholder}`}
            </span>
            <span
              className="pointer-events-none absolute right-3 top-1/2 -mt-0.5 h-0 w-0 border-x-[4px] border-x-transparent border-t-[4px] border-t-black"
              aria-hidden
            />
          </button>

          {isOpen && dropdownPosition && typeof document !== 'undefined'
            ? createPortal(
                <div
                  ref={dropdownRef}
                  role="listbox"
                  aria-label={sizeLabel}
                  style={{
                    position: 'fixed',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    zIndex: 1100,
                  }}
                  className="border border-[#d9d9d9] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
                >
                  <div className="flex shrink-0 border-b border-[#d9d9d9] bg-[#ededed]">
                    {showEddColumn ? (
                      <>
                        <div className="flex-1 border-r border-white px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
                          {dropdownColumnLabel}
                        </div>
                        <div className="flex-1 px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
                          Delivery by
                        </div>
                      </>
                    ) : (
                      <div className="w-full px-2.5 py-2.5 text-center text-[15px] font-bold leading-6 text-[#808080]">
                        {dropdownColumnLabel}
                      </div>
                    )}
                  </div>

                  <div ref={listRef} className="max-h-[220px] overflow-y-auto">
                    {sizes.map((size) => {
                      const isSelected = selectedSize?.key === size.key;

                      return (
                        <button
                          key={size.key}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          data-selected={isSelected ? 'true' : 'false'}
                          disabled={isApplying}
                          onClick={() => handleSelect(size)}
                          className={cn(
                            'w-full border-b border-[#f0f0f0] text-left transition-colors last:border-b-0',
                            isSelected ? 'bg-[rgba(89,172,89,0.15)]' : 'bg-white hover:bg-[#fafafa]'
                          )}
                        >
                          {showEddColumn ? (
                            <div className="flex min-h-[44px] items-center">
                              <div className="flex flex-1 items-center justify-center px-2.5 py-2.5 text-[15px] font-bold leading-6 text-[#1f3767]">
                                {size.value}
                              </div>
                              <div className="flex flex-1 items-center justify-center px-2.5 py-2.5">
                                <CustomizationSizeEddLabel
                                  size={size}
                                  nddAvailableSizes={nddAvailableSizes}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex min-h-[44px] items-center justify-center px-2.5 py-2.5 text-[15px] font-bold leading-6 text-[#1f3767]">
                              {size.value}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>

        {sizerLinkLabel ? (
          <button
            type="button"
            className="border-0 bg-transparent p-0 text-[13px] leading-5 text-[var(--link-blue)] hover:underline"
            onClick={onOpenSizer}
          >
            {sizerLinkLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
